@use "Styles/includes/variables";
@use "Styles/mixins";

$thumbnail-width: 124px;
$thumbnail-height: 70px;
$current-thumbnail-width: 160px;
$current-thumbnail-height: 90px;
$stage-gap: 16px;
$gutter: 16px;
$slide-radius: 10px;
// Clears vm-modal's lightbox close button.
$close-button-clearance: calc(max(16px, env(safe-area-inset-top)) + 40px + 16px);
$rail-width: 240px;
$nav-button-size: 44px;

:host {
    display: contents;
}

.vm-video-slides-viewer {
    position: relative;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: $close-button-clearance 0 max(16px, env(safe-area-inset-bottom));
    font-family: variables.$fontQuaternary;
    color: mixins.get-color(text, text-white);

    &__stage {
        flex: 1 1 0;
        min-height: 0;
        container-type: size;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: $stage-gap;
        overflow: hidden;
    }

    // Groups the slide with its desktop buttons; on mobile its slide sits directly in the stage.
    &__pane {
        display: contents;
    }

    &__nav,
    &__video-thumbnail {
        display: none;
    }

    &__slide {
        position: relative;
        flex: none;
        // Shrinks on short screens so the strip still fits.
        width: min(100% - 2 * $gutter, (100cqh - #{$current-thumbnail-height + $stage-gap}) * 16 / 9);
        aspect-ratio: 16 / 9;
        display: grid;
        place-items: center;
        overflow: hidden;
        border-radius: $slide-radius;
        background: var(--stroma-primitive-gray-800);
        // Horizontal drags belong to the swipe gesture.
        touch-action: pan-y pinch-zoom;
        user-select: none;
    }

    &__image {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        -webkit-user-drag: none;
    }

    // Transparent halves over the slide; a swipe that starts on one is still a swipe, as the
    // gesture's filterTaps swallows the click that ends a drag.
    &__tap-zone {
        position: absolute;
        inset-block: 0;
        width: 50%;
        padding: 0;
        border: 0;
        background: none;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;

        &--previous {
            left: 0;
            --nav-arrow-nudge: -8px;
        }

        &--next {
            right: 0;
            --nav-arrow-nudge: 8px;
        }

        &[aria-disabled="true"] {
            cursor: default;
        }

        &:focus-visible {
            outline: none;
        }

        // Desktop steps with the buttons below the slide.
        @include mixins.desktop-and-up {
            display: none;
        }
    }

    // Flashed at the zone's outer edge on each tap or swipe.
    &__nav-arrow {
        position: absolute;
        top: 50%;
        display: grid;
        place-items: center;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: rgba(mixins.get-color(background, bg-overlay), 0.7);
        color: mixins.get-color(text, text-white);
        opacity: 0;
        transform: translateY(-50%);
        pointer-events: none;
        animation: nav-arrow-flash 500ms ease-out;

        .vm-video-slides-viewer__tap-zone--previous > & {
            left: max(16px, env(safe-area-inset-left));
        }

        .vm-video-slides-viewer__tap-zone--next > & {
            right: max(16px, env(safe-area-inset-right));
        }

        @media (prefers-reduced-motion: reduce) {
            --nav-arrow-nudge: 0px;
        }

        vm-icon {
            width: 32px;
            height: 32px;
        }
    }

    &__thumbnails {
        align-self: stretch;
        --vm-slider-gap: 8px;
    }

    &__thumbnail-item {
        flex: none;
        align-self: center;
    }

    &__thumbnail {
        position: relative;
        display: block;
        box-sizing: border-box;
        width: $thumbnail-width;
        height: $thumbnail-height;
        padding: 0;
        border: 0;
        border-radius: $slide-radius;
        overflow: hidden;
        background: var(--stroma-primitive-gray-800);
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;

        &[aria-current="true"] {
            width: $current-thumbnail-width;
            height: $current-thumbnail-height;
            border: 4px solid mixins.get-color(background, bg-brand-solid);
        }

        // Inset, so the strip's scroller doesn't clip it.
        &:focus-visible {
            outline: 2px solid mixins.get-color(background, bg-brand-solid);
            outline-offset: -2px;
        }

        &[aria-current="true"]:focus-visible {
            outline-color: mixins.get-color(text, text-white);
        }
    }

    &__thumbnail-image {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        -webkit-user-drag: none;
    }

    &__thumbnail-number {
        position: absolute;
        left: 4px;
        bottom: 4px;
        display: grid;
        place-items: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: rgba(mixins.get-color(background, bg-overlay), 0.7);
        @include mixins.text-xs-bold;
        color: mixins.get-color(text, text-white);

        [aria-current="true"] > & {
            left: 2px;
            bottom: 2px;
            background: mixins.get-color(background, bg-brand-solid);
        }
    }

    &__info {
        flex: none;
        margin-inline: $gutter;
        padding: 24px;
        border-radius: 12px;
        background: var(--stroma-primitive-gray-800);
    }

    &__title {
        margin: 0;
        @include mixins.text-bold-medium;
        font-weight: 900;
        color: mixins.get-color(text, text-white);
    }

    &__meta {
        margin: 4px 0 0;
        @include mixins.text-xs-regular;
        color: rgba(mixins.get-color(text, text-white), 0.7);
    }

    &__watch {
        margin-top: 24px;

        vm-icon {
            width: 24px;
            height: 24px;
        }
    }

    // Shown only in the landscape layout.
    &__counter {
        display: none;
    }

    &__status {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
    }
}

// Mobile landscape: slide only.
@include mixins.tablet-and-down {
    @media (orientation: landscape) {
        .vm-video-slides-viewer {
            padding: 0;

            &__stage {
                gap: 0;
            }

            // Fills the stage so the tap zones and swipe reach the letterbox bars; the
            // image keeps the 16:9 box.
            &__slide {
                width: 100%;
                height: 100%;
                aspect-ratio: auto;
                border-radius: 0;
                background: none;
            }

            &__image {
                width: min(100%, 100cqh * 16 / 9);
                height: auto;
                aspect-ratio: 16 / 9;
            }

            &__thumbnails,
            &__info {
                display: none;
            }

            &__counter {
                display: block;
                position: absolute;
                bottom: max(16px, env(safe-area-inset-bottom));
                left: 50%;
                transform: translateX(-50%);
                margin: 0;
                padding: 4px 12px;
                border-radius: 9999px;
                background: rgba(mixins.get-color(background, bg-overlay), 0.7);
                @include mixins.text-bold-small;
                color: mixins.get-color(text, text-white);
                white-space: nowrap;
                // Taps fall through to the tap zone beneath.
                pointer-events: none;
            }
        }
    }
}

@include mixins.desktop-and-up {
    .vm-video-slides-viewer {
        width: min(90vw, 1280px);
        height: min(90dvh, 800px);
        padding: 0;
        gap: 0;
        border-radius: var(--stroma-radius-4xl);
        background: var(--stroma-color-background-subtle);
        color: var(--stroma-color-foreground-emphasized);

        &__info {
            order: -1;
            display: flex;
            align-items: center;
            gap: 16px;
            margin: 32px $close-button-clearance 32px 40px;
            border-radius: var(--stroma-radius-3xl);
            background: var(--stroma-color-background-default);
            box-shadow: var(--stroma-elevation-medium);
        }

        &__video-thumbnail {
            display: block;
            flex: none;
            width: $current-thumbnail-width;
            height: $current-thumbnail-height;
            border-radius: var(--stroma-radius-lg);
            object-fit: cover;
        }

        &__details {
            flex: 1 1 0;
            min-width: 0;
        }

        &__title {
            @include mixins.text-large-bold;
            font-weight: 900;
            color: var(--stroma-color-foreground-emphasized);
        }

        &__meta {
            @include mixins.text-regular-small;
            color: var(--stroma-color-foreground-default);
        }

        &__watch {
            margin-top: 0;

            vm-icon {
                width: 16px;
                height: 16px;
            }
        }

        &__stage {
            flex-direction: row-reverse;
            align-items: stretch;
            gap: 0;
            background: var(--stroma-color-background-default);
            border-radius: 0 0 var(--stroma-radius-4xl) var(--stroma-radius-4xl);
            box-shadow: var(--stroma-elevation-small);
        }

        &__pane {
            flex: 1 1 0;
            min-width: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: $stage-gap;
            padding: 26px 14px 14px;
            container-type: size;
        }

        &__slide {
            width: min(100cqw, (100cqh - #{$nav-button-size + $stage-gap}) * 16 / 9);
            background: var(--stroma-color-background-default);
            box-shadow: var(--stroma-elevation-large);
        }

        &__nav {
            display: flex;
            gap: 16px;
        }

        &__thumbnails {
            flex: none;
            width: $rail-width;
            border-right: 1px solid var(--stroma-color-border-subtle);
            --vm-slider-gap: 2px;
            --vm-slider-padding: 12px 8px;
        }

        &__thumbnail-item {
            align-self: stretch;
        }

        // The whole rail item: number, then thumbnail.
        &__thumbnail,
        &__thumbnail[aria-current="true"] {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            height: auto;
            padding: 6px;
            border: 0;
            border-radius: var(--stroma-radius-md);
            overflow: visible;
            background: none;
        }

        &__thumbnail[aria-current="true"] {
            background: var(--stroma-color-brand-muted);
        }

        // current row is already highlighted and image is outlined; remove focus ring
        &__thumbnail[aria-current="true"]:focus-visible {
            outline: none;
        }

        &__thumbnail-image {
            flex: none;
            box-sizing: border-box;
            width: $current-thumbnail-width;
            height: $current-thumbnail-height;
            border: 1px solid var(--stroma-color-border-subtle);
            border-radius: var(--stroma-radius-lg);

            [aria-current="true"] > & {
                border: 2px solid var(--stroma-color-brand-default);
            }
        }

        &__thumbnail-number,
        [aria-current="true"] > &__thumbnail-number {
            position: static;
            order: -1;
            width: 20px;
            background: none;
            @include mixins.text-regular-xsmall;
            text-align: right;
            color: var(--stroma-color-foreground-default);
        }

        [aria-current="true"] > &__thumbnail-number {
            font-weight: 500;
            color: var(--stroma-color-foreground-emphasized);
        }
    }
}

@keyframes nav-arrow-flash {
    20% {
        opacity: 1;
        transform: translateY(-50%);
    }

    100% {
        opacity: 0;
        transform: translate(var(--nav-arrow-nudge), -50%);
    }
}
